"use client";

import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { SaveSearchAuthDialog } from "@/components/auth/dialogs/save-search-auth-dialog";
import { Icon } from "@/components/ui/icon";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useErrorFormatter } from "@/hooks/use-error-formatter";
import { api } from "@/lib/api-client";
import type { SearchData } from "@/lib/types";
import { useAtom } from "jotai";
import { useRouter, useSearchParams } from "next/navigation";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

export function SaveSearchBanner({
  count,
  getSearchData,
}: {
  count: number;
  getSearchData: () => Promise<SearchData | null>;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, loaded } = useCurrentUser();
  const { displayErrorToast } = useErrorFormatter();
  const [filters] = useAtom(businessFiltersAtom);
  const [saving, setSaving] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  const pendingSearchDataRef = useRef<SearchData | null>(null);

  const createSavedSearchMutation = api.searches.saved.create.useMutation();
  const isUpdate = searchParams.has("searchId");

  const hasAnyFilters = useMemo(() => {
    return (
      filters.businessTypeIds.length > 0 ||
      filters.statuses.length > 0 ||
      filters.priceRange.min !== null ||
      filters.priceRange.max !== null ||
      filters.revenueRange.min !== null ||
      filters.revenueRange.max !== null ||
      filters.profitRange.min !== null ||
      filters.profitRange.max !== null ||
      filters.sdeRange.min !== null ||
      filters.sdeRange.max !== null ||
      filters.financingAvailable ||
      filters.sbaPrequalified ||
      filters.visaQualified
    );
  }, [filters]);

  if (!hasAnyFilters || isUpdate) return null;

  async function doSave() {
    const searchData = pendingSearchDataRef.current ?? (await getSearchData());
    if (!searchData) {
      setSaving(false);
      toast.error("Could not save search");
      return;
    }

    try {
      setSaving(true);
      await createSavedSearchMutation.mutateAsync(searchData);
      router.push("/my-searches");
    } catch (err) {
      displayErrorToast(err);
    } finally {
      setSaving(false);
      pendingSearchDataRef.current = null;
    }
  }

  async function handleSave() {
    if (saving) return;

    if (!user || !loaded) {
      const searchData = await getSearchData();
      pendingSearchDataRef.current = searchData;
      setShowAuthDialog(true);
      return;
    }

    await doSave();
  }

  async function handleAuthenticated() {
    setShowAuthDialog(false);
    await doSave();
  }

  return (
    <>
      <div className="relative z-10 hidden border-b border-border/70 bg-transparent px-[18px] pb-3 lg:block">
        <div className="flex items-center justify-between rounded-xl border border-border/80 bg-card/90 px-4 py-2.5 shadow-[0_1px_0_rgba(255,255,255,0.92)_inset,0_8px_24px_rgba(0,0,0,0.08)] backdrop-blur-md supports-[backdrop-filter]:bg-card/72">
          <p className="text-sm text-muted-foreground">
            <span className="font-data font-medium text-foreground">
              {count}
            </span>{" "}
            businesses match your search
          </p>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-[8px] px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
          >
            <Icon icon="Bell" className="h-4 w-4 text-muted-foreground" />
            Save this search - get alerts when new listings match
          </button>
        </div>
      </div>

      <SaveSearchAuthDialog
        open={showAuthDialog}
        onOpenChange={setShowAuthDialog}
        onAuthenticated={handleAuthenticated}
      />
    </>
  );
}
